import { createContext } from "preact";
import { useState, useContext, useEffect } from "preact/hooks";
const CacheContext = createContext(
  /** @type {Cache<any>} */
  /* @__PURE__ */ new Map()
);
function useQuery(key, fetcher) {
  const cache = useContext(CacheContext);
  const [value, setValue] = useState(getNextValue);
  useEffect(() => {
    const { status, subscribers } = getCacheEntry();
    subscribers.push(setValue);
    if (status !== "loading")
      refetch();
    return () => {
      subscribers.splice(subscribers.indexOf(setValue), 1);
      if (!subscribers.length)
        cache.delete(key);
    };
  }, [key]);
  function getNextValue() {
    const cacheEntry = getCacheEntry();
    const nextValue = (
      /** @type {Result<Data>} */
      {
        status: cacheEntry.status,
        data: cacheEntry.data,
        isLoading: cacheEntry.status === "loading",
        isSuccess: cacheEntry.status === "success",
        isError: cacheEntry.status === "error",
        setData,
        refetch
      }
    );
    if (nextValue.status === "error") {
      nextValue.error = /** @type {CacheErrorEntry<Data>} */
      cacheEntry.error;
    }
    return nextValue;
  }
  function getCacheEntry() {
    if (!cache.has(key)) {
      cache.set(key, { subscribers: [] });
    }
    return (
      /** @type {CacheEntry<Data>} */
      cache.get(key)
    );
  }
  function setCacheValue(nextCacheValuePatch) {
    const cacheEntry = getCacheEntry();
    for (const patchKey in nextCacheValuePatch) {
      cacheEntry[patchKey] = nextCacheValuePatch[patchKey];
    }
    const nextValue = getNextValue();
    cacheEntry.subscribers.forEach((setSubscriberValue) => {
      setSubscriberValue(nextValue);
    });
  }
  function setData(nextData) {
    setCacheValue({ status: "success", data: nextData });
  }
  function refetch() {
    if (key == null) {
      return;
    }
    setCacheValue({ status: "loading" });
    Promise.resolve(fetcher()).then(setData).catch((error) => {
      setCacheValue({
        status: "error",
        error
      });
    });
  }
  return value;
}
export {
  CacheContext,
  useQuery
};
